















































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { RootState } from '@/store/store';
import { ApiState, SortOrder } from '@/store/types/general.types';
import {
  BulkUpdateImageStatusManageDTO,
  BulkUpdateImageStatusSubmissionDTO,
  CreateImageDTO,
  DeleteImageDTO,
  EditedImage,
  ImageStatus,
  JbicEntityType,
  JbicGroupImageFilterOptions,
  JbicImage,
  JbicImageVersion,
  Pagination,
  UpdateJbicImage
} from '@/store/modules/admin/types/admin.types';
import { Debounce } from '@/jbi-shared/util/debounce.vue-decorator';
import BasePaginatorHoc from '@/components/base/BasePaginatorHoc.vue';
import Images from './components/Images.vue';
import UploadImagesModal from './components/UploadImagesModal.vue';
import { ToastProgrammatic } from 'buefy';
import { GetSignedUrlForUploadRequestPayload } from '@/store/modules/static-file/types/static-file.types';
import {
  GetSignedUrlForUploadResponsePayload,
  ImageFileSizeLimit
} from '@/store/modules/admin/types/group-level-attribute.types';
import ImageUploadProgress from './components/ImageUploadProgress.vue';
import EditImageSidebar from './components/EditImageSidebar.vue';
import GeneralModal from '@/components/GeneralModal.vue';
import DeclineReasonModal from './components/DeclineReasonModal.vue';
import { checkJbicPermission } from '@/utils/rbac.util';
import { JbicPermissions } from '@/store/modules/roles-and-permissions/types/roles-and-permissions.types';
import { subscribeToRealTime } from '@/utils/real-time.util';
import { useWebsocket } from '@/utils/websocket.util';

@Component({
  components: {
    BasePaginatorHoc,
    Images,
    UploadImagesModal,
    ImageUploadProgress,
    EditImageSidebar,
    GeneralModal
  }
})
export default class GroupImagesTab extends Vue {
  @Prop(Number) groupId!: number;

  renamedImages: File[] = [];
  uploadProgress: Map<string, number> = new Map();
  openUploadProgress: boolean = false;
  uploadedImagesCount: number = 0;

  @Action('staticFile/getSignedUrlForUploads')
  public getSignedUrlForUploads!: (
    d: GetSignedUrlForUploadRequestPayload
  ) => Promise<GetSignedUrlForUploadResponsePayload>;

  @Action('admin/createImage')
  public createImage!: (params: CreateImageDTO) => void;

  @State(({ admin }: RootState) => admin.apiState.createImage)
  public createImageState!: ApiState;

  @Action('admin/getJbicGroupImagesWithPagination')
  getJbicGroupImagesWithPagination!: (
    params?: JbicGroupImageFilterOptions
  ) => void;

  @State(
    ({ admin }: RootState) => admin.apiState.getJbicGroupImagesWithPagination
  )
  jbicGroupImagesWithPaginationApiState!: ApiState;

  @State(({ admin }: RootState) => admin.jbicGroupImagesWithPagination)
  jbicGroupImagesWithPagination!: Pagination<JbicImage>;

  @Action('admin/updateImageDetails')
  updateImageDetails!: (image: UpdateJbicImage) => void;

  @State(({ admin }: RootState) => admin.apiState.updateImageDetails)
  updateImageDetailsApiState!: ApiState;

  @Action('admin/deleteJbicImage')
  private deleteJbicImage!: (dto: DeleteImageDTO) => Promise<void>;

  @State(({ admin }: RootState) => admin.apiState.deleteJbicImage)
  private deleteJbicImageState!: ApiState;

  @Action('admin/updateImageStatusSubmission')
  updateImageStatusSubmission!: (
    image: BulkUpdateImageStatusSubmissionDTO
  ) => void;

  @State(({ admin }: RootState) => admin.apiState.updateImageStatusSubmission)
  updateImageStatusSubmissionApiState!: ApiState;

  @Action('admin/updateManagedImageStatus')
  updateManagedImageStatus!: (image: BulkUpdateImageStatusManageDTO) => void;

  @State(({ admin }: RootState) => admin.apiState.updateManagedImageStatus)
  updateManagedImageStatusApiState!: ApiState;

  // pagination
  public perPage = 50;
  public page = 1;
  public sortColumn: string = 'image.created_at';
  public sortOrder: SortOrder = SortOrder.DESC;
  public totalItems: number = 0;

  // filter
  public filterParams: JbicGroupImageFilterOptions = this.getInitFormParams();

  // params
  public images: JbicImage[] = [];
  public previousVersionImages: JbicImageVersion[] = [];
  public imageStatus: string = 'DRAFT';
  public currentImage: JbicImage = {
    id: 0,
    imageUrl: '',
    alternateText: '',
    caption: '',
    status: ImageStatus.DRAFT,
    groupId: 0,
    createdAt: new Date(),
    createdBy: {
      userId: 0,
      email: '',
      emailVerified: false,
      firstName: '',
      lastName: '',
      type: '',
      status: '',
      username: ''
    }
  };
  public currentImageVersion: JbicImageVersion = this.initializeImageVersion;
  public isSidebarOpen: boolean = false;
  public isSidebarReadOnly: boolean = true;
  private unsubscribeRealtime: (() => void) | undefined;

  get Images() {
    return Images;
  }

  get isJbicImageCreateRole(): boolean {
    return checkJbicPermission(JbicPermissions.IMAGE_CREATE, this.groupId);
  }

  get isJbicImageUpdateRole(): boolean {
    return checkJbicPermission(JbicPermissions.IMAGE_UPDATE, this.groupId);
  }

  get isJbicImageDeleteRole(): boolean {
    return checkJbicPermission(JbicPermissions.IMAGE_DELETE, this.groupId);
  }

  get isJbicImageViewRole(): boolean {
    return checkJbicPermission(JbicPermissions.IMAGE_READ, this.groupId);
  }

  get isJbicImageManageRole(): boolean {
    return checkJbicPermission(JbicPermissions.IMAGE_MANAGE, this.groupId);
  }

  async mounted() {
    // Check if imageStatus is in query params (from email link)
    const imageStatusFromQuery = this.$route.query.imageStatus;

    if (imageStatusFromQuery) {
      // Set the imageStatus from query param
      this.imageStatus = imageStatusFromQuery as string;
      // Reset pagination to page 1
      this.page = 1;
      // Clear search term
      this.filterParams.search = '';
      // Change to Image Gallery tab
      this.$emit('selectTab', 1);
    }
    if (!imageStatusFromQuery && this.isJbicImageManageRole) {
      this.imageStatus = ImageStatus.AWAITING_APPROVAL;
    }
    this.getJbicGroupImagesWithPaginationHandler();
    if (this.groupId) {
      this.unsubscribeRealtime = await subscribeToRealTime.call(
        this,
        'groupImage-' + this.groupId,
        async () => {
          this.getJbicGroupImagesWithPaginationHandler();
        }
      );
    }
  }

  beforeDestroy() {
    if (this.unsubscribeRealtime) {
      this.unsubscribeRealtime(); // remove listener
      const { disconnectWs } = useWebsocket.call(this);
      disconnectWs();
    }
  }

  getInitFormParams(): JbicGroupImageFilterOptions {
    return {
      groupId: this.groupId,
      status: ImageStatus.DRAFT,
      search: ''
    };
  }

  @Debounce(500)
  getJbicGroupImagesWithPaginationHandler() {
    this.getJbicGroupImagesWithPagination({
      groupId: this.filterParams.groupId,
      status: this.filterParams.status,
      search: this.filterParams.search,
      page: this.page,
      limit: this.perPage,
      sortColumn: this.sortColumn,
      sortOrder: this.sortOrder
    });
    this.refreshCount();
  }

  resetFilter() {
    this.filterParams = this.getInitFormParams();
  }

  @Watch('jbicGroupImagesWithPaginationApiState')
  onJbicGroupImagesWithPaginationApiState() {
    if (
      !this.jbicGroupImagesWithPaginationApiState ||
      this.jbicGroupImagesWithPaginationApiState.loading ||
      !this.jbicGroupImagesWithPaginationApiState.success ||
      !this.jbicGroupImagesWithPagination
    ) {
      return;
    }

    this.images = this.jbicGroupImagesWithPagination.items;
    this.totalItems = this.jbicGroupImagesWithPagination.totalItems;
  }

  @Watch('filterParams', { deep: true })
  onFilterParams() {
    this.getJbicGroupImagesWithPaginationHandler();
  }

  @Watch('imageStatus')
  onImageStatusChange() {
    this.filterParams.status = this.imageStatus as ImageStatus;
  }

  handlePaginator({ perPage, page }: { perPage: number; page: number }) {
    if (this.$route.query.tab !== 'Images') {
      return;
    }
    this.perPage = perPage || +this.$route.query.limit;
    this.page = page || +this.$route.query.page;

    this.getJbicGroupImagesWithPaginationHandler();

    this.$router.push({
      path: this.$route.path,
      query: {
        tab: 'Images',
        groupId:
          (this.$route.query.groupId as string) ||
          ((this.filterParams.groupId ?? '') as string),
        status: this.$route.query.status || this.filterParams.status!,
        search: this.$route.query.search || this.filterParams.search!,
        limit: this.perPage.toString(),
        page: this.page.toString(),
        sortColumn: (this.$route.query.sortColumn as string) || this.sortColumn,
        sortOrder: (this.$route.query.sortOrder as SortOrder) || this.sortOrder
      }
    });
  }

  handleSearchTermChange(searchTerm: string) {
    this.filterParams.search = searchTerm;
  }

  handleUploadImagesClick() {
    this.$buefy.modal.open({
      parent: this,
      component: UploadImagesModal,
      hasModalCard: true,
      trapFocus: true,
      canCancel: false,
      props: {
        groupId: this.groupId
      },
      events: {
        uploadingImages: (images: File[]) => {
          this.processFile(images);
        }
      }
    });
  }

  refreshCount() {
    this.$emit('refreshCount');
  }

  processFile(images: File[]) {
    // return/cancel if no images are uploaded
    if (!images) {
      return;
    }

    // check for any oversized file(s). Limit is 2MB
    const oversizedFile: File | undefined = images.find((file: File) => {
      return file.size > ImageFileSizeLimit;
    });
    if (oversizedFile) {
      ToastProgrammatic.open({
        message: `File '${oversizedFile.name}' has exceeded size limitation.`,
        type: 'is-danger',
        position: 'is-top'
      });
      return;
    }

    // uploadedImagesCount for success toast
    // toggleProgressBar to toggle the image upload progress bar
    this.renamedImages = [];
    this.uploadedImagesCount = 0;
    this.toggleProgressBar(true);

    images.forEach(async (image, index) => {
      // renamed the file name for consistency
      const fileName: string = `jbic_image_${new Date().getTime()}_${index}.${image.name
        .split('.')
        .pop()}`;

      // keep the file list with new file name for later use
      const renamedFile = new File([image], fileName, { type: image.type });
      this.renamedImages.push(renamedFile);
      // initiate the file into upload progress bar
      this.setUploadProgress(fileName, 0);

      // get signed url for selected file
      const signedUrlData = await this.getSignedUrlForUploads({
        fileName: fileName,
        contentType: image.type,
        groupId: this.groupId,
        entityType: 'image',
        bucket: 'jbic'
      });

      if (!signedUrlData) {
        this.showUploadToastError();
        this.setUploadProgress(fileName, -1);
        return;
      }

      // upload to gcp
      this.uploadFileToSignedUrl(renamedFile, signedUrlData).catch(() => {
        this.showUploadToastError();
        this.setUploadProgress(signedUrlData.fileName, -1);
      });
    });
  }

  uploadFileToSignedUrl(
    selectedFile: File,
    payload: GetSignedUrlForUploadResponsePayload
  ): Promise<GetSignedUrlForUploadResponsePayload> {
    const formData = new FormData();
    formData.append('file', selectedFile as Blob);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', payload.signedUrl, true);
      xhr.setRequestHeader('Content-Type', selectedFile.type);

      xhr.onload = (e) => {
        if (xhr.status < 200 || xhr.status >= 300) {
          this.setUploadProgress(payload.fileName, -1);
          return reject(new Error(`Upload failed with status ${xhr.status}`));
        }
        this.setUploadProgress(payload.fileName, 1);
        const param: CreateImageDTO = {
          imageUrl: payload.storageUrl,
          alternateText: '',
          caption: '',
          status: ImageStatus.DRAFT,
          groupId: this.groupId
        };
        this.createImage(param);
        return resolve(payload);
      };

      xhr.onerror = (error) => {
        this.setUploadProgress(payload.fileName, -1);
        return reject(error);
      };

      xhr.onprogress = (ev) => {
        this.setUploadProgress(payload.fileName, ev.loaded / ev.total);
      };

      xhr.send(selectedFile);
    });
  }

  showUploadToastError(message?: string) {
    ToastProgrammatic.open({
      message: message || 'Upload failed. Please try again.',
      type: 'is-danger',
      position: 'is-top'
    });
  }

  setUploadProgress(imageName: string, percent: number) {
    this.$set(this.uploadProgress, imageName, percent);
  }

  toggleProgressBar(toOpen: boolean) {
    // TODO: disable this first. To look into this later
    // this.openUploadProgress = toOpen;
  }

  @Watch('createImageState')
  onCreateImageState() {
    if (this.createImageState.loading || !this.createImageState.success) {
      return;
    }

    if (this.createImageState.error) {
      ToastProgrammatic.open({
        message: 'Failed while finalizing the image upload. Please try again.',
        type: 'is-danger',
        position: 'is-top'
      });
    }

    if (this.createImageState.success) {
      this.uploadedImagesCount += 1;
      ToastProgrammatic.open({
        message: `${this.uploadedImagesCount}/${this.renamedImages.length} image(s) uploaded successfully`,
        type: 'is-success',
        position: 'is-top'
      });

      this.getJbicGroupImagesWithPaginationHandler();
    }
  }

  onToggleSidebar(isOpen: boolean, isReadOnly: boolean = true) {
    this.isSidebarOpen = isOpen;
    this.isSidebarReadOnly = isReadOnly;
  }

  onToggleEditImage(image: EditedImage) {
    this.currentImage = image.images;
    this.currentImageVersion =
      image.lastReviewImages ?? this.initializeImageVersion;

    // open the sidebar with readonly = false
    this.onToggleSidebar(true, false);
  }

  onToggleViewImage(image: EditedImage) {
    this.currentImage = image.images;
    this.currentImageVersion =
      image.lastReviewImages ?? this.initializeImageVersion;

    // open the sidebar with readonly = false
    this.onToggleSidebar(true, true);
  }

  get initializeImageVersion(): JbicImageVersion {
    return {
      id: 0,
      imageId: 0,
      imageUrl: '',
      alternateText: '',
      caption: '',
      status: ImageStatus.DRAFT,
      groupId: 0,
      createdAt: new Date(),
      createdBy: {
        userId: 0,
        email: '',
        emailVerified: false,
        firstName: '',
        lastName: '',
        type: '',
        status: '',
        username: ''
      }
    };
  }

  updateImageStatusSubmissionHandler(imageIds: number[]): void {
    this.updateImageStatusSubmission({
      ids: imageIds,
      status: ImageStatus.AWAITING_APPROVAL
    });
  }

  onPublishImage(imageIds: number[]): void {
    this.updateImageStatusSubmission({
      ids: imageIds,
      status: ImageStatus.PUBLISHED
    });
  }

  onUnpublishImage(imageIds: number[]): void {
    this.updateImageStatusSubmission({
      ids: imageIds,
      status: ImageStatus.DRAFT
    });
  }

  onDeleteImage(imageIds: number[]) {
    this.$buefy.modal.open({
      parent: this,
      component: GeneralModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Delete selected image(s)?',
        modalContent:
          'This will permanently delete the selected image(s). This action cannot be undone.',
        confirmBtnText: 'Delete',
        cancelBtnText: 'Cancel'
      },
      events: {
        confirm: () => {
          this.deleteJbicImage({ ids: imageIds });
        }
      }
    });
  }

  onUpdateImageDetails(imageDetails: JbicImage) {
    // Draft: stay in Draft (details-only update, no review needed)
    // Awaiting Approval / Approved / Denied: go back to Awaiting Approval (re-review needed after edit)
    const statusAfterUpdate =
      imageDetails.status === ImageStatus.DRAFT
        ? ImageStatus.DRAFT
        : ImageStatus.AWAITING_APPROVAL;

    const updateImage: UpdateJbicImage = {
      imageId: imageDetails.id,
      alternateText: imageDetails.alternateText,
      caption: imageDetails.caption,
      status: statusAfterUpdate
    };
    this.updateImageDetails(updateImage);

    // close the sidebar
    this.onToggleSidebar(false);
  }

  onApproveImage(imageIds: number[]): void {
    this.updateManagedImageStatus({
      ids: imageIds,
      status: ImageStatus.APPROVED
    });
  }

  onDeclineImage(imageIds: number[]): void {
    this.$buefy.modal.open({
      parent: this,
      component: DeclineReasonModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        entityType: JbicEntityType.IMAGE
      },
      events: {
        confirm: (declineReason: string) => {
          const payload: BulkUpdateImageStatusManageDTO = {
            ids: imageIds,
            status: ImageStatus.DECLINED,
            declinedReason: declineReason
          };
          this.updateManagedImageStatus(payload);
        }
      }
    });
  }

  @Watch('updateImageDetailsApiState', { deep: true })
  onUpdateImageDetailsApiState() {
    if (this.updateImageDetailsApiState.error) {
      ToastProgrammatic.open({
        message: `An error occurred while updating the image details. Please try again.`,
        type: 'is-danger',
        position: 'is-top'
      });

      return;
    }

    if (
      this.updateImageDetailsApiState.loading ||
      !this.updateImageDetailsApiState.success
    ) {
      return;
    }

    ToastProgrammatic.open({
      message: `The image details have been updated successfully.`,
      type: 'is-success',
      position: 'is-top'
    });

    this.getJbicGroupImagesWithPaginationHandler();
  }

  @Watch('deleteJbicImageState', { deep: true })
  onDeleteJbicImageState() {
    if (this.deleteJbicImageState.error) {
      ToastProgrammatic.open({
        message: `An error occurred while deleting the image(s). Please try again.`,
        type: 'is-danger',
        position: 'is-top'
      });

      return;
    }

    if (
      this.deleteJbicImageState.loading ||
      !this.deleteJbicImageState.success
    ) {
      return;
    }

    ToastProgrammatic.open({
      message: `The image(s) have been deleted successfully.`,
      type: 'is-success',
      position: 'is-top'
    });

    this.getJbicGroupImagesWithPaginationHandler();
  }

  @Watch('updateImageStatusSubmissionApiState', { deep: true })
  onSubmitEventForApprovalState() {
    if (this.updateImageStatusSubmissionApiState.error) {
      ToastProgrammatic.open({
        message: `An error occurred while submitting the image(s) for approval. Please try again.`,
        type: 'is-danger',
        position: 'is-top'
      });

      return;
    }

    if (
      this.updateImageStatusSubmissionApiState.loading ||
      !this.updateImageStatusSubmissionApiState.success
    ) {
      return;
    }

    ToastProgrammatic.open({
      message: `The image(s) status has been updated successfully.`,
      type: 'is-success',
      position: 'is-top'
    });

    this.getJbicGroupImagesWithPaginationHandler();
  }

  @Watch('updateManagedImageStatusApiState', { deep: true })
  onUpdateManagedEventStatusState() {
    if (this.updateManagedImageStatusApiState.error) {
      ToastProgrammatic.open({
        message: `An error occurred while updating the image(s) status. Please try again.`,
        type: 'is-danger',
        position: 'is-top'
      });

      return;
    }

    if (
      this.updateManagedImageStatusApiState.loading ||
      !this.updateManagedImageStatusApiState.success
    ) {
      return;
    }

    ToastProgrammatic.open({
      message: `The image(s) status has been updated successfully.`,
      type: 'is-success',
      position: 'is-top'
    });

    this.getJbicGroupImagesWithPaginationHandler();
  }
}
